/**
 * CaseNotePicker — the header + Case Note client picker (R8, brief of
 * record 2026-07-06): the hero button opens this picker; the DEFAULT is
 * the nearest upcoming visit's client today, else the last-touched
 * client. Row-level + Case Note buttons pre-scope and never open this.
 *
 * The list is her caseload; for a viewer who reads every client without
 * view_workspace (a line clinician under the tenant's Everyone on staff
 * setting) Your day also passes moreClients, every other client, listed
 * after her caseload's matches, de-duplicated and without chips. The
 * default still comes from her caseload.
 *
 * Picking a client navigates to the record page's Notes tab (the
 * composer lives there, pre-scoped to the client).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Chip } from '@/components/primitives/Chip';
import { SearchField } from '@/components/primitives/SearchField';
import type { CmHomeClientWire } from '@/lib/cmHomeApi';
import { defaultComposeClient, initials, visitTodayLabel } from '@/lib/cmHome';

export interface CaseNotePickerProps {
  clients: readonly CmHomeClientWire[];
  /** Every other client the viewer may write a note on, listed after her
   *  caseload; omitted, the picker is her caseload alone. */
  moreClients?: readonly { id: string; display_name: string }[];
  tenantSlug: string;
  onClose: () => void;
}

export function CaseNotePicker({ clients, moreClients, tenantSlug, onClose }: CaseNotePickerProps): ReactNode {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const fallback = useMemo(() => defaultComposeClient(clients), [clients]);

  const q = filter.trim().toLowerCase();
  const list = useMemo(() => {
    const matches =
      q === '' ? [...clients] : clients.filter((c) => c.display_name.toLowerCase().includes(q));
    // Default first, the rest by name.
    return matches.sort((a, b) => {
      if (fallback) {
        if (a.id === fallback.id) return -1;
        if (b.id === fallback.id) return 1;
      }
      return a.display_name.localeCompare(b.display_name);
    });
  }, [clients, q, fallback]);
  const more = useMemo(() => {
    if (!moreClients) return [];
    const own = new Set(clients.map((c) => c.id));
    const seen = new Set<string>();
    const rows = moreClients.filter((c) => {
      if (own.has(c.id) || seen.has(c.id)) return false;
      seen.add(c.id);
      return q === '' || c.display_name.toLowerCase().includes(q);
    });
    return rows.sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [moreClients, clients, q]);
  const firstId = list[0]?.id ?? more[0]?.id ?? null;

  function start(clientId: string): void {
    onClose();
    navigate(`/t/${tenantSlug}/clients/${clientId}?tab=notes`);
  }

  return (
    <Dialog title="New case note" onClose={onClose} size="sm" testId="case-note-picker">
      <p className="mb-3 text-sm text-ink-soft">Who is this note about?</p>
      <SearchField
        autoFocus
        value={filter}
        onChange={setFilter}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && firstId !== null) start(firstId);
        }}
        placeholder="Find a client"
        aria-label="Find a client"
        testId="case-note-picker-search"
        className="mb-3"
      />
      <ul className="m-0 max-h-72 list-none divide-y divide-sage-100 overflow-auto p-0">
        {list.map((c) => (
          <PickerRow key={c.id} displayName={c.display_name} onPick={() => start(c.id)}>
            {c.visit_today && <Chip variant="affirm">{visitTodayLabel(c)}</Chip>}
            {!c.visit_today && fallback?.id === c.id && (
              <Chip variant="neutral">Last touched</Chip>
            )}
          </PickerRow>
        ))}
        {more.map((c) => (
          <PickerRow key={c.id} displayName={c.display_name} onPick={() => start(c.id)} />
        ))}
        {list.length === 0 && more.length === 0 && (
          <li className="py-3 text-sm text-ink-soft">We can't find that client. Try another spelling.</li>
        )}
      </ul>
    </Dialog>
  );
}

/** One client in the picker: initials, name, and her caseload's chips. */
function PickerRow({
  displayName,
  onPick,
  children,
}: {
  displayName: string;
  onPick: () => void;
  children?: ReactNode;
}): ReactNode {
  return (
    <li>
      <Button
        variant="ghost"
        size="md"
        className="w-full !justify-start gap-3 py-2 text-left"
        data-testid="picker-client"
        onClick={onPick}
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-pill bg-sage-50 text-[11px] font-bold text-sage-800"
        >
          {initials(displayName)}
        </span>
        <span className="min-w-0 flex-1 truncate">{displayName}</span>
        {children}
      </Button>
    </li>
  );
}
